// src/components/sections/ApiExplanationSection.styles.js

const getResponsiveValue = (mobile, tablet, desktop) => {
  if (typeof window === "undefined") return desktop;

  const width = window.innerWidth;
  if (width <= 640) return mobile;
  if (width <= 768) return tablet;
  return desktop;
};
const styles = {
  // Section container
  section: (isDarkMode) => ({
    padding: "6rem 0",
    backgroundColor: isDarkMode ? "#111827" : "#f8fafc",
    position: "relative",
    overflow: "hidden",
    width: "100%",
    backgroundImage: isDarkMode
      ? "linear-gradient(to bottom, #111827, #0f172a)"
      : "linear-gradient(175deg, #f8fafc, #f1f5f9)",
  }),

  // Background elements
  backgroundElements: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    overflow: "hidden",
  },

  // Gradient blobs
  gradientBlob1: {
    position: "absolute",
    top: "-20%",
    right: "-10%",
    width: "40%",
    height: "60%",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, transparent 70%)",
    filter: "blur(50px)",
  },

  gradientBlob2: {
    position: "absolute",
    bottom: "-10%",
    left: "-5%",
    width: "35%",
    height: "50%",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)",
    filter: "blur(60px)",
  },

  // Container
  container: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 2rem",
    position: "relative",
    zIndex: 1,
  },

  // Content grid - now for full width content
  contentGrid: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2rem",
  },

  // Full width content
  fullWidthContent: {
    width: "100%",
    maxWidth: "900px",
    margin: "0 auto",
    textAlign: "center",
  },

  // Feature badge
  featureBadge: (isDarkMode) => ({
    marginBottom: "1.5rem",
    display: "inline-flex",
    alignItems: "center",
    gap: "0.75rem",
    backgroundColor: isDarkMode
      ? "rgba(59, 130, 246, 0.15)"
      : "rgba(59, 130, 246, 0.1)",
    borderRadius: "9999px",
    padding: "0.5rem 1rem",
  }),

  badgeIcon: {
    backgroundColor: "#3b82f6",
    borderRadius: "9999px",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  badgeText: (isDarkMode) => ({
    fontSize: "0.875rem",
    fontWeight: 600,
    color: isDarkMode ? "#9cc2ff" : "#3b82f6",
  }),

  // Main heading
  mainHeading: {
    fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
    fontWeight: 800,
    marginBottom: "1.5rem",
    lineHeight: 1.1,
    background: "linear-gradient(90deg, #3b82f6, #60a5fa, #818cf8)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    textAlign: "center",
  },

  // Main description
  mainDescription: (isDarkMode) => ({
    fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
    color: isDarkMode ? "rgba(255,255,255,0.75)" : "rgba(31, 41, 55, 0.8)",
    lineHeight: 1.7,
    marginBottom: "2rem",
    maxWidth: "850px",
    margin: "0 auto 2rem",
  }),

  // Benefits grid - now in a 2x2 layout
  benefitsGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: getResponsiveValue("1.5rem", "2rem", "2rem"),
    marginTop: getResponsiveValue("2rem", "2.5rem", "3rem"),
    marginBottom: getResponsiveValue("2rem", "2.5rem", "3rem"),
    maxWidth: "900px",
    margin: getResponsiveValue("2rem auto", "2.5rem auto", "3rem auto"),
  },

  // Benefit item - enhanced with card style
  benefitItem: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "0.75rem",
    padding: getResponsiveValue("1.25rem", "1.5rem", "1.75rem"),
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
    position: "relative",
    overflow: "hidden",
    // Responsive width
    width: "405px",
    // width: getResponsiveValue("100%", "calc(50% - 1rem)", "calc(50% - 1rem)"),
    // maxWidth: getResponsiveValue(
    //   "100%",
    //   "calc(50% - 1rem)",
    //   "calc(50% - 1rem)"
    // ),
  },

  // Benefit header
  benefitHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: "1rem",
    marginBottom: "1.25rem",
  },

  // Benefit icon container
  benefitIconContainer: (isDarkMode) => ({
    backgroundColor: isDarkMode
      ? "rgba(59, 130, 246, 0.15)"
      : "rgba(59, 130, 246, 0.08)",
    borderRadius: "50%",
    width: "3.75rem",
    height: "3.75rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#3b82f6",
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
    transition: "all 0.3s ease",
    border: "1px solid rgba(59, 130, 246, 0.1)",
    marginTop: "0.25rem",
    "&:hover": {
      transform: "scale(1.08) rotate(5deg)",
      boxShadow: "0 6px 15px rgba(59, 130, 246, 0.25)",
    },
  }),

  // Benefit title
  benefitTitle: (isDarkMode) => ({
    fontSize: "1.35rem",
    fontWeight: 700,
    color: isDarkMode ? "#f9fafb" : "#111827",
    textAlign: "left",
    marginBottom: "0.5rem",
    lineHeight: 1.3,
  }),

  // Benefit description
  benefitDescription: (isDarkMode) => ({
    fontSize: "1rem",
    color: isDarkMode ? "rgba(255,255,255,0.7)" : "rgba(31, 41, 55, 0.7)",
    lineHeight: 1.7,
    textAlign: "left",
    marginTop: "0.75rem",
    borderTop: `1px solid ${
      isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
    }`,
    paddingTop: "0.75rem",
  }),

  // CTA container
  ctaContainer: {
    marginTop: "2rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "2rem",
    "@media (max-width: 640px)": {
      flexDirection: "column",
      gap: "1rem",
    },
  },

  // Primary button
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.75rem",
    backgroundColor: "#3b82f6",
    color: "white",
    padding: "1rem 1.75rem",
    borderRadius: "0.5rem",
    fontWeight: 600,
    fontSize: "1.125rem",
    letterSpacing: "0.01em",
    textDecoration: "none",
    boxShadow: "0 8px 15px -5px rgba(59, 130, 246, 0.4)",
    transition: "all 0.2s ease",
    borderBottom: "3px solid rgba(0, 0, 0, 0.1)",
  },

  // Secondary link
  secondaryLink: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    cursor: "pointer",
  },

  // Secondary link text
  secondaryLinkText: (isDarkMode) => ({
    color: isDarkMode ? "#9cc2ff" : "#3b82f6",
    fontWeight: 600,
    fontSize: "1rem",
  }),
};

export default styles;
